import React from "react";

import { type AppAppearanceMode, useAppAppearance } from "@/context/AppAppearanceContext";

import "./globalAppearanceToggle.css";

type GlobalAppearanceToggleProps = {
  placement?: "floating" | "inline";
  className?: string;
};

const APPEARANCE_OPTIONS: Array<{
  label: string;
  value: Extract<AppAppearanceMode, "light" | "dark">;
}> = [
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
];

function AppearanceIcon(props: {
  mode: "light" | "dark";
}): JSX.Element {
  const { mode } = props;

  if (mode === "light") {
    return (
      <svg className="app-global-appearance-toggle__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="3.2" />
        <path d="M8 1.5v1.8" />
        <path d="M8 12.7v1.8" />
        <path d="M1.5 8h1.8" />
        <path d="M12.7 8h1.8" />
        <path d="M3.5 3.5l1.3 1.3" />
        <path d="M11.2 11.2l1.3 1.3" />
        <path d="M12.5 3.5l-1.3 1.3" />
        <path d="M4.8 11.2l-1.3 1.3" />
      </svg>
    );
  }

  return (
    <svg className="app-global-appearance-toggle__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M11.7 10.3A5.7 5.7 0 0 1 5.7 4.2a5.2 5.2 0 0 1 .6-2.4 6.3 6.3 0 1 0 7.9 7.8 5.3 5.3 0 0 1-2.5.7Z" />
    </svg>
  );
}

export default function GlobalAppearanceToggle(props: GlobalAppearanceToggleProps = {}): JSX.Element {
  const { placement = "floating", className } = props;
  const { mode, resolvedAppearance, setMode } = useAppAppearance();
  const activeMode = mode === "system" ? resolvedAppearance : mode;
  const rootClassName = [
    "app-global-appearance-toggle",
    placement === "inline" ? "app-global-appearance-toggle--inline" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={rootClassName}
      data-resolved-appearance={resolvedAppearance}
      aria-label="Global appearance toggle"
    >
      <div className="app-global-appearance-toggle__row">
        {APPEARANCE_OPTIONS.map((option) => {
          const isActive = option.value === activeMode;

          return (
            <button
              key={option.value}
              type="button"
              className={[
                "app-global-appearance-toggle__option",
                isActive ? "app-global-appearance-toggle__option--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={option.label}
              onClick={() => setMode(option.value)}
            >
              <AppearanceIcon mode={option.value} />
            </button>
          );
        })}
      </div>
    </section>
  );
}